/*
  File: HelpDrawer.tsx
  Path: src/components/HelpDrawer.tsx
  Description: Dirt-simple slide-over Help for the Pro dashboard. Opens OVER the current
    page (never navigates away). Now covers the whole dashboard via a hardcoded topic
    list (see helpTopics.tsx). Behaviour:
      - Always opens to the topic LIST.
      - A search box is pinned at the top in BOTH views (list and open topic).
      - The page you're on is tagged "This page" and floated to the top of the list.
      - Click a topic to read it; "All topics" (or typing in search) returns to the list.
    Viking-minimum: no CMS, no backend. "Search" is a plain filter over the topic array.
  Author: Larry McLean / Claude
  Created: 2026-08-11
  Version: 2.2.0
  Status: ACTIVE
  Change Log (append-only):
    - 2026-08-11 - v1.0.0 - Initial single-card drawer (Import customers only). [Larry McLean / Claude]
    - 2026-08-11 - v2.0.0 - Generalized to full dashboard: topic list, always-on search,
      context "This page" tag, per-topic cards. [Larry McLean / Claude]
    - 2026-08-11 - v2.1.0 - Contact footer wired to a real support email: visible address
      + mailto link with pre-filled subject (industry standard). [Larry McLean / Claude]
    - 2026-08-11 - v2.2.0 - Contact footer pinned to the bottom of the drawer: always visible
      while the body scrolls, in both list and topic views (never stranded). [Larry McLean / Claude]
*/

import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, HelpCircle, Search, ChevronLeft, ChevronRight, Mail } from "lucide-react";
import { HELP_TOPICS, topicForPath } from "./helpTopics";
import "./HelpDrawer.css";

/** Support address shown to users. Change here to change it everywhere. */
const HELP_EMAIL = "contact@aquorix.com";
const HELP_MAILTO = `mailto:${HELP_EMAIL}?subject=${encodeURIComponent(
  "AQUORIX Help"
)}`;

/** Contact bar pinned to the bottom of the drawer — always visible, in every view. */
const HelpFooter: React.FC = () => (
  <footer className="aqx-help-footbar">
    <Mail className="aqx-help-foot-icon" aria-hidden="true" />
    <span>
      Still stuck? Email us at{" "}
      <a className="aqx-help-foot-link" href={HELP_MAILTO}>
        {HELP_EMAIL}
      </a>
    </span>
  </footer>
);

type HelpDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const HelpDrawer: React.FC<HelpDrawerProps> = ({ open, onClose }) => {
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);

  // The topic that owns the page you're currently on (may be null).
  const currentTopic = useMemo(
    () => topicForPath(location.pathname),
    [location.pathname]
  );

  // Every time the drawer opens, start clean: list view, no search text.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveId(null);
    }
  }, [open]);

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const q = query.trim().toLowerCase();

  // Filtered + ordered list. With no query, the current page's topic floats to top.
  const list = useMemo(() => {
    let items = HELP_TOPICS;
    if (q) {
      items = items.filter((t) =>
        (t.label + " " + t.keywords + " " + t.teaser).toLowerCase().includes(q)
      );
    } else if (currentTopic) {
      items = [
        currentTopic,
        ...HELP_TOPICS.filter((t) => t.id !== currentTopic.id),
      ];
    }
    return items;
  }, [q, currentTopic]);

  // Typing in search always drops you back to the list.
  const activeTopic =
    !q && activeId ? HELP_TOPICS.find((t) => t.id === activeId) || null : null;

  if (!open) return null;

  return (
    <div className="aqx-help-overlay" onMouseDown={onClose}>
      <aside
        className="aqx-help-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Help"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="aqx-help-head">
          <div className="aqx-help-title">
            <HelpCircle className="aqx-help-title-icon" aria-hidden="true" />
            <span>Help</span>
          </div>
          <button
            className="aqx-help-close"
            type="button"
            aria-label="Close help"
            onClick={onClose}
          >
            <X />
          </button>
        </header>

        {/* Search — pinned, present in both views. */}
        <div className="aqx-help-search">
          <Search className="aqx-help-search-icon" aria-hidden="true" />
          <input
            className="aqx-help-search-input"
            type="text"
            placeholder="Search help…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search help topics"
            autoFocus
          />
        </div>

        <div className="aqx-help-body">
          {activeTopic ? (
            /* ── Topic card view ──────────────────────────────── */
            <>
              <button
                className="aqx-help-back"
                type="button"
                onClick={() => setActiveId(null)}
              >
                <ChevronLeft className="aqx-help-back-icon" aria-hidden="true" />
                All topics
              </button>
              {activeTopic.body}
            </>
          ) : (
            /* ── Topic list view ──────────────────────────────── */
            <>
              {list.length === 0 ? (
                <p className="aqx-help-empty">
                  No help topics match “{query}”. Try a different word, or clear the search to see
                  everything.
                </p>
              ) : (
                <ul className="aqx-help-list">
                  {list.map((t) => {
                    const isThisPage = !q && currentTopic?.id === t.id;
                    return (
                      <li key={t.id}>
                        <button
                          className="aqx-help-row"
                          type="button"
                          onClick={() => {
                            setActiveId(t.id);
                            setQuery("");
                          }}
                        >
                          <span className="aqx-help-row-main">
                            <span className="aqx-help-row-label">
                              {t.label}
                              {isThisPage && (
                                <span className="aqx-help-thispage">This page</span>
                              )}
                            </span>
                            <span className="aqx-help-row-teaser">{t.teaser}</span>
                          </span>
                          <ChevronRight
                            className="aqx-help-row-chevron"
                            aria-hidden="true"
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>

        {/* Contact bar — pinned outside the scroll area, always visible. */}
        <HelpFooter />
      </aside>
    </div>
  );
};

export default HelpDrawer;
